import { cn } from "@cronika/ui";
import { FC, MutableRefObject, useRef, useState } from "react";

import { isLessThanCurrentTime, nowPlusOneMinute, pad2, type THms } from "./lib";

type TSegmentProps = {
  value: THms;
  /** Exclusive upper bound (24 for hours, 60 for min/sec). */
  max: number;
  type: "hours" | "minutes" | "seconds";
  invalid?: boolean;
  /** When true, clamp a past wall clock to now + 1 minute. */
  compareToNow?: boolean;
  /** IANA timezone for wall-clock comparisons (falls back to browser local when empty). */
  timezone?: string;
  onChange: (next: THms) => void;
  /** Ref exposed so InlineClock can focus this input programmatically. */
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  /** Called when the user finishes entering 2 digits, to jump to the next field. */
  onAutoAdvance?: () => void;
  /** Called on focus so the parent can track which field was entered first. */
  onFocusField?: () => void;
};

/** One directly-typable digit pair inside {@link InlineClock}. */
const Segment: FC<TSegmentProps> = ({ value, max, type, invalid, compareToNow, timezone, onChange, inputRef, onAutoAdvance, onFocusField }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;

  const editedValue = isEditing && draft !== "" ? Math.min(Number(draft), max - 1) : value[type];
  const isLessCurrentTime =
    compareToNow === true && isLessThanCurrentTime({ ...value, [type]: editedValue }, new Date(), timezone);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={isEditing ? draft : pad2(value[type])}
      // Editing the digits must not bubble to the field wrapper (which opens the
      // picker) — the point is to edit inline without the modal/dropdown.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onFocus={(e) => {
        onFocusField?.();
        setDraft(pad2(value[type]));
        e.target.select();
      }}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(-2);
        setDraft(digits);
        if (digits === "") return;

        const next: THms = { ...value, [type]: Math.min(Number(digits), max - 1) };
        if (compareToNow && isLessThanCurrentTime(next, new Date(), timezone)) {
          const clamped = nowPlusOneMinute(new Date(), timezone);
          setDraft(pad2(clamped[type]));
          onChange(clamped);
          // Auto-advance even after clamp so UX stays consistent.
          if (digits.length === 2) {
            setDraft(null);
            onAutoAdvance?.();
          }
          return;
        }
        onChange(next);
        // Auto-advance to the next segment once 2 digits are entered.
        if (digits.length === 2) {
          setDraft(null);
          onAutoAdvance?.();
        }
      }}
      className={cn(
        "w-[2ch] bg-transparent text-center tabular-nums outline-none cursor-text",
        invalid || isLessCurrentTime
          ? "text-danger"
          : isEditing
            ? "text-warning underline underline-offset-2"
            : "text-inherit",
      )}
    />
  );
};

type TInlineClockProps = {
  value: THms;
  onChange: (next: THms) => void;
  invalid?: boolean;
  /** When true (Time mode), clamp past wall-clock values to now + 1 minute. */
  compareToNow?: boolean;
  /** IANA timezone for wall-clock comparisons (falls back to browser local when empty). */
  timezone?: string;
  className?: string;
};

/**
 * Compact `HH:MM:SS` shown in the trigger field, with each pair directly
 * editable in place. Clicking a digit edits it without opening the picker;
 * clicking anywhere else on the field still opens the picker.
 * Typing 2 digits auto-advances focus: H → M → S → blur (closes the selection).
 */
export const InlineClock: FC<TInlineClockProps> = ({ value, onChange, invalid, compareToNow, timezone, className }) => {
  const hoursRef = useRef<HTMLInputElement | null>(null);
  const minutesRef = useRef<HTMLInputElement | null>(null);
  const secondsRef = useRef<HTMLInputElement | null>(null);

  // 'ltr' = H → M → S → blur; 'rtl' = S → M → H → blur.
  const directionRef = useRef<"ltr" | "rtl">("ltr");

  const focusAndSelect = (ref: MutableRefObject<HTMLInputElement | null>) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
  };

  const advanceHours = () => {
    if (directionRef.current === "ltr") focusAndSelect(minutesRef);
    else hoursRef.current?.blur();
  };
  const advanceMinutes = () => {
    if (directionRef.current === "ltr") focusAndSelect(secondsRef);
    else focusAndSelect(hoursRef);
  };
  const advanceSeconds = () => {
    if (directionRef.current === "rtl") focusAndSelect(minutesRef);
    else secondsRef.current?.blur();
  };

  return (
    <div className={cn("inline-flex items-center", className)}>
      <Segment
        value={value}
        type="hours"
        max={24}
        invalid={invalid}
        compareToNow={compareToNow}
        timezone={timezone}
        onChange={onChange}
        inputRef={hoursRef}
        onFocusField={() => { directionRef.current = "ltr"; }}
        onAutoAdvance={advanceHours}
      />
      <span className="opacity-60">:</span>
      <Segment
        value={value}
        type="minutes"
        max={60}
        invalid={invalid}
        compareToNow={compareToNow}
        timezone={timezone}
        onChange={onChange}
        inputRef={minutesRef}
        onAutoAdvance={advanceMinutes}
      />
      <span className="opacity-60">:</span>
      <Segment
        value={value}
        type="seconds"
        max={60}
        invalid={invalid}
        compareToNow={compareToNow}
        timezone={timezone}
        onChange={onChange}
        inputRef={secondsRef}
        onFocusField={() => { directionRef.current = "rtl"; }}
        onAutoAdvance={advanceSeconds}
      />
    </div>
  );
};
